import { api } from './apiClient';

// Dedupe identical in-flight requests (e.g. React 18 StrictMode dev double-effect).
const inFlight = new Map();

const tryExtractGroupsArray = (data) => {
  // Common backend wrappers:
  // - { groups: [...] }
  // - { data: { groups: [...] } }
  // - { result: { groups: [...] } }
  // - { data: { data: { groups: [...] } } }
  // - { current_week: { groups: [...] } }
  return (
    data?.groups ||
    data?.current_week?.groups ||
    data?.previous_week?.groups ||
    data?.data?.groups ||
    data?.data?.current_week?.groups ||
    data?.data?.previous_week?.groups ||
    data?.result?.groups ||
    data?.result?.current_week?.groups ||
    data?.result?.previous_week?.groups ||
    data?.data?.data?.groups ||
    data?.data?.data?.current_week?.groups ||
    data?.data?.data?.previous_week?.groups ||
    data?.result?.data?.groups ||
    data?.result?.data?.current_week?.groups ||
    data?.result?.data?.previous_week?.groups
  );
};

const tryExtractPreviousWeekGroupsArray = (data) => {
  return (
    data?.previous_week?.groups ||
    data?.data?.previous_week?.groups ||
    data?.result?.previous_week?.groups ||
    data?.data?.data?.previous_week?.groups ||
    data?.result?.data?.previous_week?.groups
  );
};

const normalizePreviousGroupsResponse = (data, fallbackCountry = '--') => {
  const groupsArray = tryExtractPreviousWeekGroupsArray(data);
  if (Array.isArray(groupsArray)) return groupsArray;
  // If backend doesn't send previous_week, return empty array.
  return [];
};

const normalizeGroupsResponse = (data, fallbackCountry = '--') => {
  const groupsArray = tryExtractGroupsArray(data);
  if (Array.isArray(groupsArray)) return groupsArray;

  // Sometimes:
  // data = { users: [...] }
  if (Array.isArray(data?.users)) {
    const country = data?.country || fallbackCountry || '--';
    const group_index = data?.group_index ?? 1;
    return [
      {
        country,
        group_index,
        users: data.users,
      },
    ];
  }

  // Sometimes:
  // data = [{ ...userFields }]
  if (Array.isArray(data)) {
    const country = data?.[0]?.country || fallbackCountry || '--';
    return [
      {
        country,
        group_index: 1,
        users: data,
      },
    ];
  }

  return [];
};

const tryExtractCurrentWeekNumber = (data) => {
  const raw =
    data?.current_week?.week_number ||
    data?.current_week_number ||
    data?.data?.current_week?.week_number ||
    data?.data?.current_week_number ||
    data?.result?.current_week?.week_number ||
    data?.result?.current_week_number ||
    data?.data?.data?.current_week?.week_number ||
    data?.data?.data?.current_week_number ||
    data?.result?.data?.current_week?.week_number ||
    data?.result?.data?.current_week_number;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const tryExtractPreviousWeekNumber = (data) => {
  const raw =
    data?.previous_week?.week_number ||
    data?.data?.previous_week?.week_number ||
    data?.result?.previous_week?.week_number ||
    data?.data?.data?.previous_week?.week_number ||
    data?.result?.data?.previous_week?.week_number;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const fetchWeeklyLeaderboard = async ({ weekNumber, country }) => {
  try {
    const normalizedCountry =
      typeof country === 'string' ? country.trim() : country;

    const params = {};

    const nWeek =
      typeof weekNumber === 'number' ? Number(weekNumber) : NaN;
    if (Number.isFinite(nWeek) && nWeek > 0) {
      params.week_number = nWeek;
    }
    if (normalizedCountry) {
      params.country = normalizedCountry;
    }

    const key = JSON.stringify(params);
    const existing = inFlight.get(key);
    if (existing) return await existing;

    const requestPromise = (async () => {
      const response = await api.get('/api/v1/admin/weekly-leaderboard', {
        params,
      });

      // Helps debug "no data" issues when backend response shape changes.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('weekly-leaderboard response:', response.data);
      }

      // Keep normalization flexible based on backend shape.
      const currentGroups = normalizeGroupsResponse(
        response.data,
        normalizedCountry || '--'
      );
      const previousGroups = normalizePreviousGroupsResponse(
        response.data,
        normalizedCountry || '--'
      );
      const currentWeekNumber = tryExtractCurrentWeekNumber(response.data);
      const previousWeekNumber = tryExtractPreviousWeekNumber(response.data);

      // If backend returns an explanatory message but we couldn't extract users/groups,
      // surface the message to the UI instead of showing a blank/empty table.
      if (currentGroups.length === 0) {
        const msg =
          response.data?.message ||
          response.data?.error ||
          response.data?.errors?.[0]?.message ||
          response.data?.errors?.[0] ||
          null;
        if (typeof msg === 'string' && msg.trim()) {
          // Some backends return a "success" message even when there are no rows.
          // Treat that as empty state, not an error.
          const lower = msg.trim().toLowerCase();
          const looksLikeSuccess =
            lower.includes('success') || lower.includes('fetched successfully');
          if (!looksLikeSuccess) {
            throw new Error(msg.trim());
          }
        }
      }

      return {
        groups: currentGroups,
        previousGroups,
        currentWeekNumber,
        previousWeekNumber,
      };
    })();

    inFlight.set(key, requestPromise);
    try {
      return await requestPromise;
    } finally {
      inFlight.delete(key);
    }
  } catch (error) {
    if (error.response) {
      const message =
        error.response.data?.message ||
        `Server error: ${error.response.status}`;
      throw new Error(message);
    }

    if (error.request) {
      throw new Error(
        'Unable to reach the server. Please check your internet connection or try again later.'
      );
    }

    throw new Error(error.message || 'An unexpected error occurred');
  }
};

